import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SouthEastRoundedIcon from "@mui/icons-material/SouthEastRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DataGridTable from "../../../components/DataGridTabel";
import { convertToIndianFormat } from "../../../utils/functions";
import { copyTransactionIdToClipboard } from "../../../utils/clipboard";
import { dateConverter } from "../../../utils/dateConveter";
import { useSnackbar } from "notistack";
import {
  computeLedgerTotals,
  formatLedgerType,
  formatTransactionId,
  getLedgerBalanceTotal,
  getLedgerFreeBalance,
  getLedgerFrozenBalance,
  getLedgerListingLabel,
  getLedgerStatus,
  isCreditEntry,
  formatLedgerStatus,
  formatPoolType,
} from "./ledgerHelpers";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import ExportCsvDialog from "../../../components/modals/exportCsvDialog";
import walletServices from "../../../services/walletServices";
const LEDGER_TYPES = [
  "DEPOSIT_BANK",
  "DEPOSIT_UPI",
  "DEPOSIT_RZ",
  "INVESTMENT_BANK",
  "INVESTMENT_RZ",
  "INVESTMENT_UPI",
  "REINVEST",
  "REINVEST_REVERSED",
  "FRAX_CONVERTED",
  "RCO_REGISTERED",
  "REFUND_SURPLUS",
  "REFUND_TRANSACTION",
  "REFUND_PROPERTY",
  "REFUND_AUCTION",
  "CASHBACK_FRAX_FREE",
  "WITHDRAWAL_INITIATED",
  "WITHDRAWAL_REVERSED",
];

const STATUS_OPTIONS = ["", "POSTED", "PENDING", "REJECTED", "CANCELLED"];
const POOL_TYPES = ["FREE", "FROZEN"];
const EMPTY_FILTERS = { search: "", pool: "", type: "", status: "" };

function LedgerFilterSelect({ value, onChange, emptyLabel, renderValue, children }) {
  const anchorRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState();

  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return undefined;

    const updateWidth = () => setMenuWidth(el.offsetWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={anchorRef} sx={{ width: "100%", minWidth: 0 }}>
      <TextField
        select
        fullWidth
        size="small"
        value={value}
        onChange={onChange}
        sx={{
          "& .MuiInputBase-root": {
            minWidth: 0,
            width: "100%",
          },
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => (selected ? renderValue(selected) : emptyLabel),
          MenuProps: {
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            PaperProps: menuWidth ? { style: { width: menuWidth } } : undefined,
          },
        }}
      >
        <MenuItem value="">{emptyLabel}</MenuItem>
        {children}
      </TextField>
    </Box>
  );
}

const ledgerGridSx = {
  marginTop: 0,
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    justifyContent: "center",
  },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    py: 0.5,
  },
  "& .MuiDataGrid-cell[data-field='type']": {
    whiteSpace: "normal !important",
    lineHeight: 1.3,
    overflow: "visible",
    alignItems: "center",
  },
  "& .MuiDataGrid-cell--textLeft": {
    textAlign: "center",
  },
  "& .MuiDataGrid-container--top [role=row]": {
    backgroundColor: "#fafafa",
  },
};

function StatusChip({ status }) {
  const isPending = String(status).toLowerCase() === "pending";
  const isRejected = String(status).toLowerCase() === "rejected" || String(status).toLowerCase() == "cancelled";
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 700,
        bgcolor: (theme) =>
          alpha(isPending ? theme.palette.warning.main : isRejected ? theme.palette.error.main : theme.palette.success.main, 0.14),
        color: isPending ? "warning.dark" : isRejected ? "error.dark" : "success.dark",
      }}
    />
  );
}

function SummaryStatCard({ label, value, valueColor }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "divider", height: "100%" }}>
      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            mt: 0.75,
            fontWeight: 800,
            fontSize: "1.5rem",
            lineHeight: 1.1,
            color: valueColor || "text.primary",
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

const BALANCE_COLORS = {
  available: "#22C55E",
  frozen: "#FF382D",
};

function BalancePill({ label, value, color }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 0.85,
        py: 0.35,
        borderRadius: 10,
        bgcolor: (theme) => alpha(color, 0.1),
        border: "1px solid",
        borderColor: alpha(color, 0.22),
        maxWidth: "100%",
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: "0.68rem", lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={800} sx={{ fontSize: "0.68rem", lineHeight: 1, whiteSpace: "nowrap" }}>
        {convertToIndianFormat(value)}
      </Typography>
    </Stack>
  );
}

function LedgerBalanceCell({ row, align = "center" }) {
  const available = getLedgerFreeBalance(row);
  const frozen = getLedgerFrozenBalance(row);
  const total = getLedgerBalanceTotal(row);
  const isStart = align === "flex-start";
  const isEnd = align === "flex-end";
  const stackAlign = isStart ? "flex-start" : isEnd ? "flex-end" : "center";

  const tooltipContent = (
    <Stack spacing={0.35}>
      <Typography variant="caption" fontWeight={700}>
        Balance after this transaction
      </Typography>
      <Typography variant="caption">Total wallet balance: {convertToIndianFormat(total)}</Typography>
      <Typography variant="caption">Available (liquid): {convertToIndianFormat(available)}</Typography>
      <Typography variant="caption">Frozen (committed): {convertToIndianFormat(frozen)}</Typography>
    </Stack>
  );

  const content = (
    <Stack spacing={0.6} alignItems={stackAlign} sx={{ py: 0.25, width: "100%", minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={800}
        sx={{
          lineHeight: 1.15,
          textAlign: isStart ? "left" : isEnd ? "right" : "center",
          width: "100%",
        }}
      >
        {convertToIndianFormat(total)}
      </Typography>
      <Stack
        direction="row"
        spacing={0.5}
        flexWrap="nowrap"
        justifyContent={isStart ? "flex-start" : isEnd ? "flex-end" : "center"}
        sx={{ width: "100%" }}
      >
        <BalancePill label="Avail." value={available} color={BALANCE_COLORS.available} />
        {frozen > 0 && <BalancePill label="Frozen" value={frozen} color={BALANCE_COLORS.frozen} />}
      </Stack>
    </Stack>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box sx={{ width: "100%", cursor: "default" }}>{content}</Box>
    </Tooltip>
  );
}

function PoolChip({ pool }) {
  if (!pool) return null;
  const poolLabel = formatPoolType(pool);
  let color = "default";
  if (pool === "FREE") color = "success";
  else if (pool === "FROZEN") color = "error";

  return (
    <Chip
      label={poolLabel}
      size="small"
      color={color}
      variant="outlined"
      sx={{
        fontWeight: 700,
        fontSize: "0.72rem",
        letterSpacing: 0.2,
        minWidth: 64,
        textTransform: "uppercase",
        borderRadius: 1.5,
        borderWidth: 1.5,
        height: 24,
      }}
    />
  );
}

function maskLedgerAccountNumber(accountNumber) {
  const digits = String(accountNumber || "").replace(/\D/g, "");
  if (!digits) return "••••";
  return `•••• ${digits.slice(-4)}`;
}

/**
 * Presentational UI for withdrawal bank details.
 * Wire `bankDetails` + show condition in the parent (withdrawal types only).
 *
 * bankDetails: { bankName, accountNumber, ifsc, accHolderName }
 */
function LedgerWithdrawalBankDetails({ bankDetails, compact = false }) {
  if (!bankDetails) return null;

  const bankName = bankDetails?.bankName || "Bank";
  const accountMasked = maskLedgerAccountNumber(bankDetails?.accountNumber);
  const ifsc = bankDetails?.ifsc || "-";
  const holderName = bankDetails?.accHolderName || "-";

  return (
    <Stack minWidth={0}>
      <Typography
        variant={compact ? "caption" : "body2"}
        sx={{ fontWeight: 800, lineHeight: 1.3 }}
        noWrap={compact}
      >
        {bankName}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap={compact}>
        {accountMasked}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap={compact}>
        IFSC: {ifsc}
      </Typography>
      {!compact && (
        <Typography variant="caption" color="text.secondary">
          Name: {holderName}
        </Typography>
      )}
    </Stack>
  );
}

function TransactionMobileCard({ row }) {
  const credit = isCreditEntry(row);
  const status = getLedgerStatus(row);
  const listingLabel = getLedgerListingLabel(row);
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, borderColor: "divider" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: (theme) =>
                alpha(credit && row?.status !== "REJECTED" ? theme.palette.success.main : theme.palette.error.main, 0.12),
              color: credit && row?.status !== "REJECTED" ? "success.main" : "error.main",
              flexShrink: 0,
            }}
          >
            {credit && row?.status !== "REJECTED" ? <SouthEastRoundedIcon fontSize="small" /> : row?.status === "REJECTED" ? <CancelRoundedIcon fontSize="small" /> : <NorthEastRoundedIcon fontSize="small" />}
          </Box>
          <Stack spacing={0.75} flex={1} minWidth={0}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800 }} noWrap>
                {formatTransactionId(row)}
              </Typography>
              <Tooltip title="Copy Transaction ID" placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyTransactionIdToClipboard({ row, enqueueSnackbar });
                  }}
                  sx={{ flexShrink: 0 }}
                >
                  <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight={600}>
                {formatLedgerType(row?.type)}
              </Typography>
              <PoolChip pool={row?.pool} />
            </Stack>
            {/* Withdrawal: pass bankDetails when type is withdrawal */}
            {row?.type === "WITHDRAWAL_INITIATED" && <LedgerWithdrawalBankDetails bankDetails={row?.bankDetails} />}
            {listingLabel && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {listingLabel}
              </Typography>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ pt: 0.5 }}>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Transaction amount
                </Typography>
                {row?.status === "REJECTED" ? (
                  <Typography sx={{ fontWeight: 800, color: "text.primary" }}>
                    {convertToIndianFormat(row?.amount ?? 0)}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: credit && row?.status === "POSTED" || row?.status === "PENDING" ? "success.main" : "text.primary",
                    }}
                  >
                    {credit && row?.status === "POSTED" || row?.status === "PENDING" ? "+" : "-"} {convertToIndianFormat(row?.amount ?? 0)}
                  </Typography>
                )}
              </Stack>
              <Stack spacing={0.25} alignItems="flex-end" sx={{ minWidth: 0, maxWidth: "58%" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Wallet balance
                </Typography>
                <LedgerBalanceCell row={row} align="flex-end" />
              </Stack>
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.25 }}>
              <StatusChip status={status} />
              <Typography variant="caption" color="text.secondary">
                {dateConverter({ value: row?.createdAt, momentIsTrue: true, dateFormat: "DD MMM YYYY" })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function exportLedgerCsv(rows) {
  const headers = ["Transaction", "Type", "Listing", "Date", "Status", "Direction", "Amount"];
  const lines = rows.map((row) => {
    const credit = isCreditEntry(row);
    return [
      formatTransactionId(row),
      formatLedgerType(row?.type),
      getLedgerListingLabel(row),
      dateConverter({ value: row?.createdAt, momentIsTrue: true, dateFormat: "DD MMM YYYY" }),
      getLedgerStatus(row),
      credit ? "Credit" : "Debit",
      row?.amount ?? 0,
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(",");
  });

  const blob = new Blob([[headers.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "wallet-transactions.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function LedgerTab({
  rows = [],
  total = 0,
  ledgerType,
  poolFilter = "",
  onChangePoolFilter = () => { },
  onChangeType,
  search = "",
  onChangeSearch = () => { },
  statusFilter = "",
  onChangeStatus = () => { },
  loading = false,
  paginationModel = { page: 0, pageSize: 10 },
  onPaginationModelChange = () => { },
  totalCredit = 0,
  totalDebit = 0,
  availableAmount = 0,
  frozenAmount = 0,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const { page, pageSize } = paginationModel;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const isLastPage = (page + 1) * pageSize >= total;

  const [draftFilters, setDraftFilters] = useState({
    search,
    pool: poolFilter,
    type: ledgerType,
    status: statusFilter,
  });

  const handleChangeFilters = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    onChangeSearch(draftFilters.search);
    onChangePoolFilter(draftFilters.pool);
    onChangeType(draftFilters.type);
    onChangeStatus(draftFilters.status);
    onPaginationModelChange({ ...paginationModel, page: 0 });
  };

  const hasFilters =
    Boolean(draftFilters.search || draftFilters.pool || draftFilters.type || draftFilters.status) ||
    Boolean(search || poolFilter || ledgerType || statusFilter);

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    onChangeSearch("");
    onChangePoolFilter("");
    onChangeType("");
    onChangeStatus("");
    onPaginationModelChange({ ...paginationModel, page: 0 });
  };

  const handleExportCsv = async (scope) => {
    if (scope === "all") {
      const res = await walletServices.getWalletLedger({
        type: ledgerType || undefined,
        pool: poolFilter || undefined,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        page: 1,
        pageSize: total || pageSize,
      });
      const payload = Array.isArray(res?.data?.items) ? res.data : res;
      exportLedgerCsv(payload?.items || []);
      return;
    }

    exportLedgerCsv(rows);
  };
  const columns = useMemo(
    () => [
      {
        field: "transaction",
        headerName: "Transaction",
        flex: 1,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const credit = isCreditEntry(params.row);
          return (
            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center" sx={{ py: 1, width: "100%" }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: (t) =>
                    alpha(credit && !["REJECTED", "CANCELLED"].includes(params?.row?.status) ? t.palette.success.main : t.palette.error.main, 0.12),
                  color: credit && !["REJECTED", "CANCELLED"].includes(params?.row?.status) ? "success.main" : "error.main",
                  flexShrink: 0,
                }}
              >
                {(credit && !["REJECTED", "CANCELLED"].includes(params?.row?.status)) ?
                  <SouthEastRoundedIcon sx={{ fontSize: 18 }} /> :
                  ["REJECTED", "CANCELLED"].includes(params?.row?.status) ?
                    <CancelRoundedIcon sx={{ fontSize: 18 }} /> :
                    <NorthEastRoundedIcon sx={{ fontSize: 18 }} />}
              </Box>
              <Stack spacing={0.15} minWidth={0}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={800} noWrap sx={{ minWidth: 0 }}>
                    {formatTransactionId(params.row)}
                  </Typography>
                  <Tooltip title="Copy Transaction ID" placement="top">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyTransactionIdToClipboard({ row: params.row, enqueueSnackbar });
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          );
        },
      },
      {
        field: "poolType",
        headerName: "Pool",
        flex: 0.55,
        minWidth: 90,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
            <PoolChip pool={params.row?.pool} />
          </Box>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        flex: 1.25,
        minWidth: 180,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const listingLabel = getLedgerListingLabel(params.row);
          const typeLabel = formatLedgerType(params.row?.type);
          return (
            <Tooltip title={listingLabel ? `${typeLabel}${listingLabel ? ` · ${listingLabel}` : ""}` : typeLabel} placement="top">
              <Stack
                spacing={0.15}
                sx={{
                  py: 0.5,
                  px: 0.5,
                  width: "100%",
                  alignItems: "center",
                  textAlign: "center",
                  whiteSpace: "normal",
                  overflow: "visible",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    lineHeight: 1.25,
                  }}
                >
                  {typeLabel}
                </Typography>
                {/* Withdrawal: pass bankDetails when type is withdrawal */}
                {params.row?.type === "WITHDRAWAL_INITIATED" && <LedgerWithdrawalBankDetails bankDetails={params.row?.bankDetails} compact />}
                {listingLabel && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      lineHeight: 1.2,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {listingLabel}
                  </Typography>
                )}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Date",
        flex: 0.75,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            {dateConverter({ value: params.row?.createdAt, momentIsTrue: true, dateFormat: "DD MMM YYYY" })}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => <StatusChip status={getLedgerStatus(params.row)} />,
      },
      {
        field: "amount",
        headerName: "Amount",
        flex: 0.8,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const credit = isCreditEntry(params.row);
          if (["REJECTED", "CANCELLED"].includes(params?.row?.status)) {
            return (
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  width: "100%",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                {convertToIndianFormat(params.row?.amount ?? 0)}
              </Typography>
            );
          }
          return (
            <Typography
              sx={{
                fontWeight: 800,
                color: credit ? "success.main" : "text.primary",
                width: "100%",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              {credit ? "+" : "-"} {convertToIndianFormat(params.row?.amount ?? 0)}
            </Typography>
          );
        },
      },
      {
        field: "total",
        headerName: "Balance",
        flex: 1,
        minWidth: 148,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%", px: 0.5 }}>
            <LedgerBalanceCell row={params.row} />
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.5}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryStatCard label="Total Transactions" value={total} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryStatCard
              label="Total Deposits"
              value={convertToIndianFormat(totalCredit ?? 0)}
              valueColor="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryStatCard
              label="Total Debits"
              value={convertToIndianFormat(totalDebit ?? 0)}
              valueColor="error.main"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryStatCard
              label="Available Amount"
              value={convertToIndianFormat(availableAmount ?? 0)}
              valueColor={BALANCE_COLORS.available}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryStatCard
              label="Frozen Amount"
              value={convertToIndianFormat(frozenAmount ?? 0)}
              valueColor={BALANCE_COLORS.frozen}
            />
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "divider" }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ lg: "center" }}
              sx={{ mb: 2.5 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Transaction History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A ledger of every movement in your wallet.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => setExportDialogOpen(true)}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, alignSelf: { xs: "stretch", lg: "center" } }}
              >
                Export CSV
              </Button>
              <ExportCsvDialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                currentPageCount={rows.length}
                totalCount={total}
                itemLabel="transaction"
                description="Choose which transactions to download as a CSV file."
                onExport={handleExportCsv}
              />
            </Stack>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search Transaction ID"
                  value={draftFilters.search}
                  onChange={(e) => handleChangeFilters("search", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleApplyFilters();
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      minWidth: 0,
                      width: "100%",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                <LedgerFilterSelect
                  value={draftFilters.pool}
                  onChange={(e) => handleChangeFilters("pool", e.target.value)}
                  emptyLabel="All pools"
                  renderValue={(selected) => formatPoolType(selected)}
                >
                  {POOL_TYPES.map((p) => (
                    <MenuItem key={p} value={p}>
                      {formatPoolType(p)}
                    </MenuItem>
                  ))}
                </LedgerFilterSelect>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                <LedgerFilterSelect
                  value={draftFilters.type}
                  onChange={(e) => handleChangeFilters("type", e.target.value)}
                  emptyLabel="All types"
                  renderValue={(selected) => formatLedgerType(selected)}
                >
                  {LEDGER_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {formatLedgerType(t)}
                    </MenuItem>
                  ))}
                </LedgerFilterSelect>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                <LedgerFilterSelect
                  value={draftFilters.status}
                  onChange={(e) => handleChangeFilters("status", e.target.value)}
                  emptyLabel="All status"
                  renderValue={(selected) => formatLedgerStatus(selected)}
                >
                  {STATUS_OPTIONS.filter(Boolean).map((status) => (
                    <MenuItem key={status} value={status}>
                      {formatLedgerStatus(status)}
                    </MenuItem>
                  ))}
                </LedgerFilterSelect>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={handleApplyFilters}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      py: 1,
                    }}
                    startIcon={<FilterListRoundedIcon />}
                  >
                    Apply
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                    disabled={loading || !hasFilters}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      py: 1,
                    }}
                    startIcon={<FilterAltOffRoundedIcon />}
                  >
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {isMobile ? (
              <Stack spacing={1.25}>
                {rows.map((row) => (
                  <TransactionMobileCard key={row?.id || row?._id} row={row} />
                ))}
                {rows.length === 0 && !loading && (
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                    No ledger entries found.
                  </Typography>
                )}
                {total > 0 && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={loading || page <= 0}
                      onClick={() => onPaginationModelChange({ ...paginationModel, page: page - 1 })}
                    >
                      Previous
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Page {page + 1} of {pageCount}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={loading || isLastPage}
                      onClick={() => onPaginationModelChange({ ...paginationModel, page: page + 1 })}
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </Stack>
            ) : (
              <>
                <DataGridTable
                  initialColumns={columns}
                  initialRows={rows}
                  initialPageSize={pageSize}
                  initialRowHeight={96}
                  serverMode
                  rowCount={total}
                  paginationModel={paginationModel}
                  onPaginationModelChange={onPaginationModelChange}
                  loading={loading}
                  getRowId={(row) => row.id ?? row._id}
                  hideToolbar
                  sx={ledgerGridSx}
                />
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Page {page + 1} of {pageCount}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={loading || page <= 0}
                      onClick={() => onPaginationModelChange({ ...paginationModel, page: page - 1 })}
                    >
                      Previous
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={loading || isLastPage}
                      onClick={() => onPaginationModelChange({ ...paginationModel, page: page + 1 })}
                    >
                      Next
                    </Button>
                  </Stack>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
